import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Download, TrendingUp } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api, { formatError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ClaimThread } from "@/components/ClaimThread";

const LINE_COLORS = ["#2D7E7E", "#E8A13A", "#3FA796", "#C05746", "#6B7FD7"];

const FILTERS = ["", "open", "resolved", "rejected"];

const claimLabel = (c, t) => {
  if (c.type === "no_show") return t("claims.typeNoShow");
  if (c.type === "disagreement") return t(`claims.d_${c.reason}`);
  return t(`claims.r_${c.reason}`);
};

const statusBadge = (s, t) => {
  const map = { open: "bg-yellow-100 text-yellow-700", resolved: "bg-emeraude/15 text-emeraude-dark", rejected: "bg-red-100 text-red-600" };
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${map[s] || "bg-sand text-muted2"}`}>{t(`litiges.${s || "open"}`)}</span>;
};

export function AdminClaimsPanel() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [health, setHealth] = useState(null);
  const [island, setIsland] = useState("");
  const [trends, setTrends] = useState(null);
  const [period, setPeriod] = useState(12);
  const [cronStatus, setCronStatus] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/claims", { params: filter ? { status: filter } : {} }).then((r) => setRows(r.data.claims)).catch(() => {});
  }, [filter]);
  const loadHealth = useCallback(() => api.get("/admin/claims/health", { params: island ? { island } : {} }).then((r) => setHealth(r.data)).catch(() => {}), [island]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHealth(); }, [loadHealth]);
  useEffect(() => { api.get("/admin/claims/trends", { params: { period } }).then((r) => setTrends(r.data)).catch(() => {}); }, [period]);
  useEffect(() => { api.get("/admin/cron-status").then((r) => setCronStatus(r.data)).catch(() => {}); }, []);

  const setStatus = async (row, status) => {
    try { await api.put(`/admin/claims/${row.id}/${row.party}/status`, { status }); toast.success(t(`litiges.${status}`)); load(); loadHealth(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const sendMsg = async (row, text) => {
    try { await api.post(`/claims/${row.id}/${row.party}/message`, { text }); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/claims/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "claims.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const exportHealth = async () => {
    try {
      const res = await api.get("/admin/claims/health/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "claims-health.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const quickReplies = [
    { label: t("claims.qrAckL"), text: t("claims.qrAck") },
    { label: t("claims.qrPhotosL"), text: t("claims.qrPhotos") },
    { label: t("claims.qrContactL"), text: t("claims.qrContact") },
    { label: t("claims.qrRefundL"), text: t("claims.qrRefund") },
  ];

  return (
    <div data-testid="admin-claims-panel">
      {health && (
        <div className="mb-5 space-y-3" data-testid="claims-health">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-heading font-bold text-lg text-ink">{t("claims.hTitle")}</h3>
            <select data-testid="claims-health-island" value={island} onChange={(e) => setIsland(e.target.value)} className="h-9 rounded-full border border-border bg-white px-3 text-sm font-semibold capitalize">
              <option value="">{t("claims.hAllIslands")}</option>
              {(health.by_island || []).map((b) => <option key={b.island} value={b.island} className="capitalize">{b.island}</option>)}
            </select>
            <Button size="sm" variant="outline" data-testid="claims-health-export" onClick={exportHealth} className="rounded-full h-9"><Download className="w-3.5 h-3.5 mr-1" /> {t("claims.hExport")}</Button>
          </div>
          {health.long_open > 0 && (
            <div data-testid="claims-longopen-alert" className="flex items-center gap-2 bg-yellow-50 border border-yellow-300 rounded-xl p-3 text-sm text-yellow-800 font-body">
              <AlertTriangle className="w-4 h-4 shrink-0" /> <span><span className="font-bold">{health.long_open}</span> {t("claims.hLongOpen")}</span>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { k: "hOpen", v: health.open, cls: "text-yellow-700" },
              { k: "hResolved", v: health.resolved, cls: "text-emeraude-dark" },
              { k: "hRate", v: `${health.resolution_rate}%`, cls: "text-canard-dark" },
              { k: "hAvg", v: `${health.avg_resolution_hours} ${t("claims.hHours")}`, cls: "text-ink" },
            ].map((s) => (
              <div key={s.k} data-testid={`claims-health-${s.k}`} className="bg-white rounded-2xl border border-border p-4 shadow-card">
                <p className="text-xs font-bold uppercase text-muted2">{t(`claims.${s.k}`)}</p>
                <p className={`text-2xl font-heading font-extrabold mt-1 ${s.cls}`}>{s.v}</p>
              </div>
            ))}
          </div>
          {cronStatus?.claims_digest?.last_run && (
            <p data-testid="cron-status" className="text-xs text-muted2 font-body mt-3">
              {t("claims.cronLast")} : <span className="font-semibold text-ink">{new Date(cronStatus.claims_digest.last_run).toLocaleString()}</span>
              {typeof cronStatus.claims_digest.overdue === "number" ? ` (${cronStatus.claims_digest.overdue})` : ""}
            </p>
          )}
        </div>
      )}
      {trends?.data?.length > 0 && trends.islands.length > 0 && (
        <div data-testid="claims-trends" className="bg-white rounded-2xl border border-border p-4 shadow-card mb-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><TrendingUp className="w-5 h-5 text-canard" /> {t("claims.trendsTitle")}</h3>
            <div className="flex gap-1.5">
              {[6, 12].map((m) => (
                <button key={m} data-testid={`trends-period-${m}`} onClick={() => setPeriod(m)}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${period === m ? "bg-canard text-white" : "bg-sand border border-border text-ink"}`}>
                  {m} {t("claims.monthsShort")}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trends.data} margin={{ top: 5, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              {trends.islands.map((isl, i) => (
                <Line key={isl} type="monotone" dataKey={isl} stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {FILTERS.map((f) => (
          <button key={f || "all"} data-testid={`claim-filter-${f || "all"}`} onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${filter === f ? "bg-canard text-white" : "bg-white border border-border text-ink"}`}>
            {f ? t(`litiges.${f}`) : t("litiges.all")}
          </button>
        ))}
        <Button size="sm" variant="outline" data-testid="claims-export-csv" onClick={exportCsv} className="rounded-full h-8 ml-auto"><Download className="w-3.5 h-3.5 mr-1" /> {t("claims.exportCsv")}</Button>
      </div>
      {!rows.length ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-border text-muted2 font-body">{t("claims.empty")}</div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <div key={`${row.id}-${row.party}`} data-testid={`admin-claim-row-${row.id}-${row.party}`} className="bg-white rounded-2xl shadow-card border border-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${row.party === "client" ? "bg-emeraude/15 text-emeraude-dark" : "bg-canard/15 text-canard-dark"}`}>
                      {row.party === "client" ? t("claims.clientReport") : t("claims.loueurReport")}
                    </span>
                    <span className="font-heading font-bold text-canard-dark text-sm">{row.ref}</span>
                    {statusBadge(row.claim?.status, t)}
                  </div>
                  <p className="text-sm text-ink font-body mt-1.5 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-red-500" /> <span className="font-bold">{claimLabel(row.claim, t)}</span></p>
                  {row.claim?.details && <p className="text-xs text-muted2 font-body mt-1">{row.claim.details}</p>}
                  <p className="text-xs text-muted2 font-body mt-1">{row.vehicle_name} · {t("claims.partyLoueur")}: {row.loueur_name} · {t("claims.partyClient")}: {row.driver_name}</p>
                  {(row.claim?.photos || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {row.claim.photos.map((p) => (
                        <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="block w-20 h-16 rounded-lg overflow-hidden border border-border">
                          <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                {row.claim?.status === "open" && (
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" data-testid={`resolve-claim-${row.id}-${row.party}`} onClick={() => setStatus(row, "resolved")} className="rounded-full h-8 text-emeraude-dark border-emeraude/30"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />{t("litiges.resolve")}</Button>
                    <Button size="sm" variant="outline" data-testid={`reject-claim-${row.id}-${row.party}`} onClick={() => setStatus(row, "rejected")} className="rounded-full h-8 text-destructive border-destructive/30"><XCircle className="w-3.5 h-3.5 mr-1" />{t("litiges.reject")}</Button>
                  </div>
                )}
              </div>
              <ClaimThread messages={row.claim?.messages} onSend={(txt) => sendMsg(row, txt)} quickReplies={quickReplies} testid={`admin-claim-thread-${row.id}-${row.party}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
